import _ from "lodash";
import React, { useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import classes from "./DeadlinesV2AddEditDetails.scss";
import classNames from "classnames";

import {
  TextAreaInput,
  DatePicker,
  ToggleSwitch,
  Alert,
} from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  AssessmentOutlined,
  CalendarScheduleOutlined,
  ClipboardTickOutlined,
  PlaceholderFileOutlined,
  WorksheetClipboardOutlined,
  OneDriveColored,
  GoogleDriveColored,
  LinkOutlined,
  DeviceDesktopOutlined,
} from "@toddle-edu/ds-icons";
import { CreateDropDown } from "Attachment/index";
import { generateRandomId } from "services/generateRandomId";
import { isSubmissionTypeIBDefined } from "Projects/modules/Utils";
import ProjectsAttachmentList from "Projects/components/ProjectsAttachmentList";
import { SUBMISSION_TYPE_DRAFT } from "Projects/Constants/stringConstants";
import moment from "moment";
import { openLinkInNewTab } from "modules/Services";
import { getSubmissionAttachmentExtraIconButtons } from "Projects/modules/ProjectModules";

const styles = {
  attachmentContainerStyle: {
    gap: "12px",
    gridTemplateColumns: "1fr",
    marginBottom: "4px",
  },
  resourcesAttachmentContainerStyle: {
    marginBottom: "16px",
  },
  createButtonComponentStyle: {
    icon: "AddCircleOutlined",
    size: "medium",
    type: "ICON_BUTTON_V2",
    variant: "plain",
  },
};

const DROP_DOWN_OPTIONS = [
  {
    key: "upload_device",
    label: "upload_device",
    icon: <DeviceDesktopOutlined />,
  },
  { key: "add_link", label: "add_link", icon: <LinkOutlined /> },
  {
    key: "add_google_drive",
    label: "add_google_drive",
    icon: <GoogleDriveColored />,
  },
  { key: "add_one_drive", label: "add_one_drive", icon: <OneDriveColored /> },
];

const DeadlinesV2AddEditDetails = props => {
  const {
    deadlineDetails,
    onUpdateDeadline,
    mode = "edit",
    containerStyle,
    projectTemplate,
    openLinkInNewTab,
    onValidationChange,
    testId,
  } = props;

  const {
    dueOn,
    description,
    title,
    closesOn,
    isStudentSubmissionEnabled,
    templateAttachments = [],
    resources = [],
    submission,
  } = deadlineDetails;
  const { type: submissionType } = submission || {};

  const isViewMode = mode === "view";
  const isIbDefinedSubmission = isSubmissionTypeIBDefined({
    submissionType,
  });
  const isSubmissionTypeDraft = submissionType === SUBMISSION_TYPE_DRAFT;

  const onClickOpenInNewTab = ({ e, attachment }) => {
    const attachmentUrl = _.get(attachment, "url", "");
    e.stopPropagation();
    if (attachmentUrl) openLinkInNewTab({ url: attachmentUrl });
  };

  const getExtraButtonsForAttachments = ({ attachment }) => {
    return getSubmissionAttachmentExtraIconButtons({
      attachment,
      t,
      onClickOpenInNewTab,
    });
  };

  const showSubmissionTemplate = useMemo(() => {
    if (isIbDefinedSubmission) {
      let isTemplateEnabledProject = false;

      _.forEach(
        _.get(projectTemplate, "body.setupConfig.stepsList"),
        listItem => {
          _.forEach(listItem?.items, item => {
            if (item?.key === "setup_project_report_template_step") {
              isTemplateEnabledProject = true;
            }
          });
        }
      );

      return (
        (isSubmissionTypeDraft && isTemplateEnabledProject) ||
        _.size(templateAttachments) > 0
      );
    } else {
      return isStudentSubmissionEnabled;
    }
  }, [
    isIbDefinedSubmission,
    isStudentSubmissionEnabled,
    isSubmissionTypeDraft,
    projectTemplate,
  ]);

  const { t } = useI18n(["common", "project"]);

  // State for tracking date validation errors
  const [hasClosesOnError, setHasClosesOnError] = useState(false);

  // Enhanced onChange handler for closesOn date with validation
  const handleClosesOnChange = useCallback(
    date => {
      // Update the date first
      onUpdateDeadline({ key: "closesOn", value: date });

      // Validate and update error state
      if (!date || !dueOn) {
        setHasClosesOnError(false);
        onValidationChange?.({ hasClosesOnError: false });
        return;
      }

      const isValid = moment(date).isSameOrAfter(moment(dueOn));
      const hasError = !isValid;
      setHasClosesOnError(hasError);
      onValidationChange?.({ hasClosesOnError: hasError });
    },
    [onUpdateDeadline, dueOn, onValidationChange]
  );

  const onDeleteAttachment = ({ attachmentId, key }) => {
    switch (key) {
      case "resources":
        onUpdateDeadline({
          key: "resources",
          value: _.filter(resources, item => item.id !== attachmentId),
        });
        break;
      case "templateAttachments":
        onUpdateDeadline({
          key: "templateAttachments",
          value: _.filter(
            templateAttachments,
            item => item.id !== attachmentId
          ),
        });
        break;
    }
  };

  const onRenameAttachment = ({ attachmentId, newName, key }) => {
    switch (key) {
      case "resources":
        onUpdateDeadline({
          key: "resources",
          value: _.map(resources, item => {
            if (item.id === attachmentId) {
              return {
                ...item,
                name: newName,
              };
            }
            return item;
          }),
        });
        break;
      case "templateAttachments":
        onUpdateDeadline({
          key: "templateAttachments",
          value: _.map(templateAttachments, item => {
            if (item.id === attachmentId) {
              return {
                ...item,
                name: newName,
              };
            }
            return item;
          }),
        });
        break;
    }
  };

  const isResourcesEmptyWithViewMode = isViewMode && _.size(resources) == 0;

  const resourcesSize = _.size(resources);
  const templateAttachmentsSize = _.size(templateAttachments);

  const resourcesInnerContainerStyles = classNames({
    [classes.resourcesInnerContainerWithAttachments]: resourcesSize > 0,
    [classes.resourcesInnerContainerWithoutAttachments]: resourcesSize == 0,
    [classes.resourcesInnerContainerInViewMode]: isViewMode,
  });

  const templateAttachmentsInnerContainerStyles = classNames({
    [classes.submissionTemplateInnerContainerWithAttachments]:
      templateAttachmentsSize > 0,
    [classes.submissionTemplateInnerContainerWithoutAttachments]:
      templateAttachmentsSize == 0,
    [classes.submissionTemplateInnerContainerInViewMode]: isViewMode,
  });

  const isEditMode = mode === "edit";

  return (
    <div className={classes.container} style={containerStyle}>
      {isIbDefinedSubmission && !isViewMode && (
        <div className={classes.alertContainer}>
          <Alert message={t("project:pre_created_deadline_alert_banner")} />
        </div>
      )}
      <div className={classes.titleContainer}>
        {isViewMode ? (
          <h4>{title}</h4>
        ) : (
          <TextAreaInput
            dsVersion="2.0"
            variant="plain"
            placeholder={t("common:add_with_label_asterisk", {
              label: t("common:title"),
            })}
            style={{
              fontSize: "3.2rem",
              fontWeight: "700",
              lineHeight: "4.4rem",
              border: "none",
              padding: "0px",
              backgroundColor: "transparent",
            }}
            minRows={1}
            showMaxLength={false}
            value={title}
            onChange={e => {
              onUpdateDeadline({
                key: "title",
                value: _.trimStart(e.target.value),
              });
            }}
            testId={`${testId}-title`}
          />
        )}
      </div>
      <div className={classes.dateContainerWrapper}>
        <div className={classes.dateContainer}>
          <div className={classes.dateText}>
            <CalendarScheduleOutlined variant="subtle" />
            <span className="text-label">{t("common:due_on")}</span>
          </div>
          <DatePicker
            placeholder={
              isViewMode
                ? t("common:no_with_label", {
                    label: t("common:due_date"),
                  })
                : t("common:select_date_and_time")
            }
            dsVersion="2.0"
            showTimePicker={true}
            variant="inline"
            value={dueOn}
            onChange={date => {
              onUpdateDeadline({ key: "dueOn", value: date });
            }}
            isClearable={true}
            disabled={isViewMode}
            key={isViewMode ? dueOn : "dueOn"}
            initialTime={moment("00:00", "hh:mm:ss A")}
            testId={`${testId}-dueOn`}
          />
        </div>

        <div className={classes.dateContainer}>
          <div className={classes.dateText}>
            <CalendarScheduleOutlined variant="subtle" />
            <span className="text-label">{t("common:closes_on")}</span>
          </div>
          <div className={classes.datePickerWrapper}>
            <DatePicker
              placeholder={
                isViewMode
                  ? t("common:no_with_label", {
                      label: t("common:due_date"),
                    })
                  : dueOn
                  ? t("common:select_date_and_time")
                  : t("project:select_due_date_first")
              }
              dsVersion="2.0"
              showTimePicker={true}
              variant="inline"
              value={closesOn}
              onChange={handleClosesOnChange}
              isClearable={true}
              hasError={hasClosesOnError && !isViewMode}
              disabled={
                isViewMode ||
                !dueOn ||
                (!isStudentSubmissionEnabled && !isIbDefinedSubmission)
              }
              key={isViewMode ? closesOn : "closesOn"}
              initialTime={dueOn && moment(dueOn, "hh:mm:ss A")}
              testId={`${testId}-closesOn`}
            />
            {hasClosesOnError && !isViewMode && (
              <div className={classNames("text-body-s", classes.errorMessage)}>
                {t("project:close_date_error_message")}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={classes.submissionContainerWrapper}>
        <div
          className={classNames({
            [classes.descriptionContainer]: true,
            [classes.descriptionContainerViewMode]: isViewMode,
          })}
        >
          <div className={classes.descriptionText}>
            <WorksheetClipboardOutlined variant="subtle" />
            <span className="text-label">{t("common:description")}</span>
          </div>
          {isViewMode ? (
            description ? (
              <pre
                className={classNames("text-label", classes.preTagResetStyles)}
                dir="auto"
              >
                {description}
              </pre>
            ) : (
              <div className={classNames("text-body", classes.subtleText)}>
                {t("common:no_description_added")}
              </div>
            )
          ) : (
            <TextAreaInput
              dsVersion="2.0"
              placeholder={t("common:enter_description_here")}
              value={description}
              maxRows={7}
              onChange={e => {
                onUpdateDeadline({
                  key: "description",
                  value: _.trimStart(e.target.value),
                });
              }}
              testId={`${testId}-description`}
            />
          )}
        </div>
        {!isIbDefinedSubmission && (
          <div className={classes.resourcesContainer}>
            <div className={resourcesInnerContainerStyles}>
              <div className={classes.resources}>
                <PlaceholderFileOutlined variant="subtle" />
                <span className="text-label">{t("common:resources")}</span>
              </div>
              {!isViewMode ? (
                <CreateDropDown
                  multiple={true}
                  isNewDropdown={true}
                  addAttachment={attachments => {
                    onUpdateDeadline({
                      key: "resources",
                      value: [
                        ...resources,
                        ..._.map(attachments, item => {
                          return { ...item, id: generateRandomId() };
                        }),
                      ],
                    });
                  }}
                  dsVersion="2.0"
                  createButtonComponentConfig={
                    styles.createButtonComponentStyle
                  }
                  dropDownOptions={DROP_DOWN_OPTIONS}
                  testId={`${testId}-addResourceDropdown`}
                />
              ) : null}
            </div>
            {isResourcesEmptyWithViewMode ? (
              <div className={classNames("text-body", classes.subtleText)}>
                {t("project:no_resources_added")}
              </div>
            ) : (
              resourcesSize > 0 && (
                <ProjectsAttachmentList
                  attachmentContainerStyle={{
                    ...styles.attachmentContainerStyle,
                    ...(!isViewMode &&
                      styles.resourcesAttachmentContainerStyle),
                  }}
                  value={resources}
                  showCreateDropDown={false}
                  shouldCallMutation={false}
                  mode={mode}
                  createDropdownComponent={<span></span>}
                  onDeleteAttachmentCallback={({ attachmentId }) => {
                    onDeleteAttachment({
                      attachmentId,
                      key: "resources",
                    });
                  }}
                  renameAttachmentCallBack={({ attachmentId, newName }) => {
                    onRenameAttachment({
                      attachmentId,
                      newName,
                      key: "resources",
                    });
                  }}
                  showAttachmentInfo={false}
                  showSimilarity={false}
                  getAttachmentExtraIconButtons={getExtraButtonsForAttachments}
                  showAttachmentExtraIconButtons={isEditMode}
                  showNewTabOption={!isEditMode}
                  showIconButton={!isEditMode}
                />
              )
            )}
          </div>
        )}

        <div
          className={classNames({
            [classes.submissionContainer]: true,
            [classes.submissionContainerInViewMode]: isViewMode,
          })}
        >
          <div className={classes.submission}>
            <ClipboardTickOutlined variant="subtle" />
            <span className="text-label">{t("common:submission")}</span>
          </div>
          <ToggleSwitch
            disabled={isViewMode || isIbDefinedSubmission}
            size={"large"}
            dsVersion="2.0"
            checked={isStudentSubmissionEnabled}
            onChange={() => {
              onUpdateDeadline({
                key: "isStudentSubmissionEnabled",
                value: !isStudentSubmissionEnabled,
              });
              if (isStudentSubmissionEnabled && !isIbDefinedSubmission) {
                onUpdateDeadline({
                  key: "closesOn",
                  value: null,
                });
              }
            }}
            testId={`${testId}-studentSubmissionEnabledToggle`}
          />
        </div>

        {showSubmissionTemplate ? (
          <div className={classes.submissionTemplateContainer}>
            <div className={templateAttachmentsInnerContainerStyles}>
              <div className={classes.submissionTemplate}>
                <AssessmentOutlined variant="subtle" />
                <span className="text-label">
                  {t("project:submission_template")}
                </span>
              </div>
              {isSubmissionTypeDraft ? (
                <div>
                  <i>{t("project:pre_created_deadline_template_text")}</i>
                </div>
              ) : !isViewMode && !isIbDefinedSubmission ? (
                <CreateDropDown
                  multiple={true}
                  isNewDropdown={true}
                  addAttachment={attachments => {
                    onUpdateDeadline({
                      key: "templateAttachments",
                      value: [
                        ...templateAttachments,
                        ..._.map(attachments, item => {
                          return { ...item, id: generateRandomId() };
                        }),
                      ],
                    });
                  }}
                  dsVersion="2.0"
                  createButtonComponentConfig={
                    styles.createButtonComponentStyle
                  }
                  dropDownOptions={_.filter(
                    DROP_DOWN_OPTIONS,
                    item => item.key !== "add_link"
                  )}
                />
              ) : null}
            </div>

            {isViewMode && _.size(templateAttachments) == 0 ? (
              <div className={classNames("text-body", classes.subtleText)}>
                {t("project:no_submission_template_added")}
              </div>
            ) : _.size(templateAttachments) > 0 ? (
              <ProjectsAttachmentList
                attachmentContainerStyle={{
                  ...styles.attachmentContainerStyle,
                  marginBottom: isViewMode ? "20px" : "8px",
                }}
                value={templateAttachments}
                showCreateDropDown={false}
                shouldCallMutation={false}
                mode={isIbDefinedSubmission ? "view" : mode}
                onDeleteAttachmentCallback={({ attachmentId }) => {
                  onDeleteAttachment({
                    attachmentId,
                    key: "templateAttachments",
                  });
                }}
                renameAttachmentCallBack={({ attachmentId, newName }) => {
                  onRenameAttachment({
                    attachmentId,
                    newName,
                    key: "templateAttachments",
                  });
                }}
                showSimilarity={false}
                getAttachmentExtraIconButtons={getExtraButtonsForAttachments}
                showAttachmentExtraIconButtons={isEditMode}
                showNewTabOption={!isEditMode}
                showIconButton={!isEditMode}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const mapActionCreators = {
  openLinkInNewTab,
};

export default connect(null, mapActionCreators)(DeadlinesV2AddEditDetails);
